/**
 * Post-processing stack.
 *
 * Constraint from the brief: bloom and distortion must never obscure a
 * projectile or the parry timing. So bloom runs on a high luminance threshold
 * (only genuine emissive magic blooms, not lit stone), depth of field is a
 * high-preset extra with a far focus so mid-range projectiles stay sharp, and
 * chromatic aberration is a fraction of a pixel.
 */
import { useMemo } from 'react';
import {
  Bloom,
  ChromaticAberration,
  DepthOfField,
  EffectComposer,
  N8AO,
  SMAA,
  ToneMapping,
  Vignette,
} from '@react-three/postprocessing';
import { BlendFunction, ToneMappingMode } from 'postprocessing';
import * as THREE from 'three';
import { useSettings } from '../gameplay/settingsStore';

export function Postfx() {
  const quality = useSettings((s) => s.quality);
  const software = useSettings((s) => s.softwareRenderer);
  const caOffset = useMemo(() => new THREE.Vector2(0.0006, 0.0004), []);

  // On a software rasteriser the composer alone can cost hundreds of
  // milliseconds a frame. Tone mapping still happens in the renderer, so the
  // image stays correct — it just loses its bloom and vignette.
  if (software) return null;

  return (
    <EffectComposer multisampling={0} enableNormalPass={quality.ssao}>
      {quality.ssao ? (
        <N8AO aoRadius={1.6} intensity={2.4} distanceFalloff={0.9} color="#0a0d18" halfRes />
      ) : (
        <></>
      )}
      <Bloom
        intensity={quality.bloomIntensity}
        luminanceThreshold={0.62}
        luminanceSmoothing={0.22}
        mipmapBlur
        radius={0.62}
      />
      {quality.depthOfField ? (
        // Focused on the rival's stance, not on the wand: a shallow foreground
        // blur is decoration, but blurring the duellist you are reading would
        // break the readability order the design insists on. `focusDistance` is
        // normalised over the camera's near..far range, so 0.12 ≈ 14 m.
        <DepthOfField focusDistance={0.12} focalLength={0.05} bokehScale={1.0} height={480} />
      ) : (
        <></>
      )}
      {quality.chromaticAberration ? (
        <ChromaticAberration
          offset={caOffset}
          radialModulation
          modulationOffset={0.35}
          blendFunction={BlendFunction.NORMAL}
        />
      ) : (
        <></>
      )}
      <Vignette eskil={false} offset={0.22} darkness={0.82} />
      <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
      <SMAA />
    </EffectComposer>
  );
}
